import { FragmentOf, readFragment } from "gql.tada";
import { MetadataFragment } from "@queries/fragments/MetadataFragment";
import { Helmet } from "react-helmet-async";
import { useGlobalContext } from "@utils/GlobalContext";
import { useLocation } from "react-router";

export const MetadataHelmet = ({
  pageInfo,
}: {
  pageInfo: {
    slug: string | null;
    metadata: FragmentOf<typeof MetadataFragment> | null;
  };
}) => {
  const location = useLocation();
  const { locale, lockedLocales, unlockedLocales } = useGlobalContext();
  const metadata = readFragment(MetadataFragment, pageInfo.metadata);
  // Editorial noindex switch (PageMetadata.indexPage in Contentful; pages
  // without metadata default to indexed). The sitemap omits these pages too
  // (generateSitemapFile).
  const shouldNoIndex = metadata?.indexPage === false;

  return (
    <Helmet>
      <title>{metadata?.title}</title>
      {shouldNoIndex && <meta name="robots" content="noindex" />}
      {!!metadata?.description && (
        <meta name="description" content={metadata?.description} />
      )}
      <link
        rel="canonical"
        href={import.meta.env.VITE_ROOT_URL + location.pathname}
      />
      <meta property="og:locale" content={locale.replace("-", "_")} />
      <link
        key={locale}
        rel="alternate"
        hrefLang="x-default"
        href={`${import.meta.env.VITE_ROOT_URL}/en-us/${location.pathname
          .split("/")
          .slice(2)
          .join("/")}`}
      />
      {[
        ...unlockedLocales,
        ...(location.pathname.endsWith("/get-connected") ? lockedLocales : []),
      ]
        .filter((anotherLocale) => anotherLocale !== locale)
        .map((locale) => (
          <link
            key={locale}
            rel="alternate"
            hrefLang={locale}
            href={`${
              import.meta.env.VITE_ROOT_URL
            }/${locale.toLocaleLowerCase()}/${location.pathname
              .split("/")
              .slice(2)
              .join("/")}`}
          />
        ))}
    </Helmet>
  );
};
